import { EventEmitter } from 'eventemitter3';

// simple helper function for improving readability
// this way we can avoid https://developer.mozilla.org/en-US/docs/Glossary/IIFE functions
// in the code
function executeSoon(fn, errorHandler, cleanupFn) {
    // biome-ignore lint/nursery/noFloatingPromises: intentional fire-and-forget async execution
    (async () => {
        try {
            await fn();
        }
        catch (err) {
            console.error('Error while executing background task', err);
            errorHandler?.(err);
        }
        finally {
            cleanupFn?.();
        }
    })();
}

class AudioPlayer extends EventEmitter {
    audioWorkletPromise;
    audioContext;
    mainGainNode;
    trackNodes = new Map();
    optimalDelayMillis;
    constructor(sampleRate) {
        super();
        this.audioContext = new AudioContext({ sampleRate });
        this.mainGainNode = this.audioContext.createGain();
        this.mainGainNode.connect(this.audioContext.destination);
        this.audioContext.addEventListener('statechange', () => {
            if (this.audioContext.state === 'suspended') {
                this.onAudioContextSuspendedOrInterrupted();
            }
            if (this.audioContext.state === 'interrupted') {
                this.onAudioContextSuspendedOrInterrupted();
            }
            if (this.audioContext.state === 'running') {
                this.onAudioContextRunning();
            }
        });
    }
    /**
     * Feed a decoded audio packet. Creates a new worklet node when trackId changes.
     */
    feedAudioPacket(packet) {
        let trackNode = this.trackNodes.get(packet.trackId);
        if (!trackNode) {
            trackNode = this.createNewWorkletNode(packet.trackId);
            this.trackNodes.set(packet.trackId, trackNode);
            this.emit('trackAdded', packet.trackId);
            if (this.optimalDelayMillis !== undefined) {
                trackNode.workletNode.port.postMessage({
                    type: 'startAtOptimalDelayMillis',
                    optimalDelayMillis: this.optimalDelayMillis,
                });
            }
        }
        trackNode.workletNode.port.postMessage({
            type: 'feedAudioFrame',
            decodedAudioData: packet.audioData,
        }, packet.audioData.channels.map((channel) => channel.buffer));
        /**
         * @todo Fix `endOfTrack` handling. We `postMessage` audio data but then `removeTrack`
         * immediately, disconnecting the worklet before it can play. Since `postMessage` is
         * async, `endOfTrack` audio may be buffered but never heard.
         */
        if (packet.endOfTrack) {
            this.removeTrack(packet.trackId);
        }
    }
    onAudioContextRunning() {
        this.emit('audioContextRunning');
    }
    onAudioContextSuspendedOrInterrupted() {
        this.emit('audioContextStopped');
    }
    async init() {
        if (!this.audioWorkletPromise) {
            this.audioWorkletPromise = this.audioContext.audioWorklet.addModule(new URL('./worklets/audioplayer.worklet.js', import.meta.url)
                .href);
        }
        await this.audioWorkletPromise;
    }
    // This will start audio playback `optimalDelayMillis` milliseconds
    // from the end of the audio buffer, if there is enough data
    // in the buffer. If there is not enough data, it will start
    // buffering the audio and then start playback when enough data
    // is available.
    startAtOptimalDelayMillis(optimalDelayMillis) {
        if (this.optimalDelayMillis === optimalDelayMillis) {
            return;
        }
        this.optimalDelayMillis = optimalDelayMillis;
        for (const trackNode of this.trackNodes.values()) {
            trackNode.workletNode.port.postMessage({
                type: 'startAtOptimalDelayMillis',
                optimalDelayMillis,
            });
        }
    }
    async reset() {
        const tasks = [];
        for (const trackNode of this.trackNodes.values()) {
            const promise = new Promise((resolve) => {
                const handler = (e) => {
                    if (e.data.type === 'resetCompleted') {
                        trackNode.workletNode.port.removeEventListener('message', handler);
                        resolve();
                    }
                };
                trackNode.workletNode.port.addEventListener('message', handler);
                trackNode.workletNode.port.postMessage({ type: 'reset' });
            });
            tasks.push(promise);
        }
        await Promise.all(tasks);
        this.optimalDelayMillis = undefined;
        this.emit('workletResetCompleted');
    }
    async unmute() {
        // try resuming twice to handle the case where
        // the audio context is interrupted
        try {
            await this.audioContext.resume();
        }
        catch (_error) {
            await this.audioContext.resume();
        }
        if (this.audioContext.state !== 'running') {
            throw new Error('Failed to unmute AudioContext');
        }
    }
    async mute() {
        await this.audioContext.suspend();
    }
    async dispose() {
        for (const trackId of this.trackNodes.keys()) {
            this.removeTrack(trackId);
        }
        await this.audioContext.close();
    }
    /**
     * Creates a new audio worklet node and gain node for a specific track.
     *
     * This method assumes that `init()` has been called and awaited before invocation,
     * ensuring the worklet module is properly loaded and the AudioContext is initialized.
     */
    createNewWorkletNode(trackId) {
        const workletNode = new AudioWorkletNode(this.audioContext, 'audio-player');
        const gainNode = this.audioContext.createGain();
        workletNode.connect(gainNode);
        gainNode.connect(this.mainGainNode);
        workletNode.port.onmessage = (e) => {
            if (e.data.type === 'purged') ;
            if (e.data.type === 'played') {
                const presentationTimeMicros = Number(e.data.frame.timestamp) +
                    Number(e.data.frame.duration);
                this.emit('newReferenceTimes', trackId, presentationTimeMicros, e.data.realTimeMillis);
            }
            if (e.data.type === 'resetCompleted') ;
        };
        return { workletNode, gainNode };
    }
    removeTrack(trackId) {
        const trackNode = this.trackNodes.get(trackId);
        if (!trackNode) {
            return;
        }
        trackNode.workletNode.port.onmessage = null;
        try {
            trackNode.workletNode.disconnect();
            trackNode.gainNode.disconnect();
        }
        catch (_) {
            // ignore
        }
        this.trackNodes.delete(trackId);
        this.emit('trackRemoved', trackId);
    }
    getActiveTrackIds() {
        return [...this.trackNodes.keys()];
    }
    /**
     * Set the gain for a specific track (0.0 to 1.0+).
     */
    setTrackGain(trackId, gain) {
        const trackNode = this.trackNodes.get(trackId);
        if (trackNode && trackNode.gainNode.gain.value !== gain) {
            trackNode.gainNode.gain.value = gain;
            this.emit('trackGainChanged', trackId, gain);
        }
    }
    /**
     * Set the main output gain (0.0 to 1.0+).
     */
    setMainGain(gain) {
        if (this.mainGainNode.gain.value !== gain) {
            this.mainGainNode.gain.value = gain;
            this.emit('mainGainChanged', gain);
        }
    }
}

/**
 * Generates a piece of sine-wave audio with phase continuity across multiple channels.
 *
 * @param frequency - The frequency of the sine wave in Hz
 * @param duration - The duration of the audio in seconds
 * @param sampleRate - The sample rate in Hz (samples per second)
 * @param numberOfChannels - The number of audio channels to generate
 * @param phaseIn - The initial phase offset in radians (default: 0)
 */
function generateSineWaveWithPhase(frequency, duration, sampleRate, numberOfChannels, phaseIn = 0) {
    const samples = Math.floor(duration * sampleRate);
    const channels = [...Array(numberOfChannels)].map(() => new Float32Array(samples));
    const step = (2 * Math.PI * frequency) / sampleRate;
    let phase = phaseIn;
    for (let i = 0; i < samples; i++) {
        const sample = Math.sin(phase) * 0.3;
        for (let channel = 0; channel < numberOfChannels; channel++) {
            channels[channel][i] = sample;
        }
        phase += step;
    }
    // Wrap phase to [0, 2π) to prevent unbounded growth
    const phaseOut = phase % (2 * Math.PI);
    return { channels, phaseOut };
}

// TODO move this to some utility package so that the file is available
// for all packages in the monorepo
const DEFAULT_CURRENCY_DECIMAL_COUNT = 2;
// Stripe's currency data as of September 2025 (https://docs.stripe.com/currencies)
const CURRENCY_RULES = {
    zeroDecimals: [
        'BIF',
        'CLP',
        'DJF',
        'GNF',
        'JPY',
        'KMF',
        'KRW',
        'MGA',
        'PYG',
        'RWF',
        'UGX',
        'VND',
        'VUV',
        'XAF',
        'XOF',
        'XPF',
    ],
    minimumPriceUnitAmounts: {
        AED: 500,
        AUD: 200,
        BGN: 200,
        BRL: 500,
        CAD: 200,
        CHF: 100,
        CZK: 2000,
        DKK: 500,
        EUR: 100,
        GBP: 100,
        GIP: 100,
        HKD: 1000,
        HUF: 40000,
        JPY: 200,
        MXN: 2000,
        MYR: 500,
        NOK: 1000,
        NZD: 200,
        PLN: 500,
        RON: 500,
        SEK: 1000,
        SGD: 100,
        THB: 4000,
        USD: 100,
    },
};
const getCurrencyDecimalCount = (currency) => {
    return CURRENCY_RULES.zeroDecimals.includes(currency.toUpperCase())
        ? 0
        : DEFAULT_CURRENCY_DECIMAL_COUNT;
};
const getPriceRange = (currency) => {
    const DEFAULT_MULTIPLIER = 5;
    const MAX_MULTIPLIER = 50;
    const minUnitAmount = CURRENCY_RULES.minimumPriceUnitAmounts[currency.toUpperCase()];
    if (minUnitAmount === undefined) {
        throw new Error(`Currency ${currency} not supported`);
    }
    return {
        defaultUnitAmount: minUnitAmount * DEFAULT_MULTIPLIER,
        minUnitAmount,
        maxUnitAmount: minUnitAmount * MAX_MULTIPLIER,
    };
};
const getFullAmount = (unitAmount, currency) => {
    const divisor = 10 ** getCurrencyDecimalCount(currency);
    return unitAmount / divisor;
};
const getUnitAmount = (fullAmount, currency) => {
    const multiplier = 10 ** getCurrencyDecimalCount(currency);
    return fullAmount * multiplier;
};
const formatUnitAmount = (unitAmount, currency, options) => {
    const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        ...options,
    });
    return formatter.format(getFullAmount(unitAmount, currency));
};
const formatUnitAmountWithoutSymbol = (unitAmount, currency) => {
    const decimalCount = getCurrencyDecimalCount(currency);
    return getFullAmount(unitAmount, currency).toFixed(decimalCount);
};

export { AudioPlayer, executeSoon, formatUnitAmount, formatUnitAmountWithoutSymbol, generateSineWaveWithPhase, getCurrencyDecimalCount, getFullAmount, getPriceRange, getUnitAmount };
//# sourceMappingURL=exports.mjs.map
